/**
 * The corpse harvest — the scythe's half of crafting (issue #8).
 *
 * ## Why this file exists
 *
 * `meta/crafting.ts` is a complete material sink: a pouch, a stack cap, ten
 * recipes, a shortfall message. It had no way to *fill*. `harvestMaterial` was
 * the only writer of `run.inventory.materials` in the whole codebase and had
 * zero callers outside `meta/`, so every recipe input was priced in matter that
 * could not enter a pouch — a recipe you can unlock, read, and never once
 * afford. `tests/reachability.test.ts` is the assertion that says so.
 *
 * ## Why it is the scythe and only the scythe
 *
 * The obvious fix is "enemy dies -> drop material", and it is the wrong one. It
 * would turn the reachability test green while making `reaper_scythe`'s authored
 * sentence — "Hits behind you on every swing, and harvests corpses into pooled
 * BLOOD" — a decoration on a verb every weapon in the game performs. A class
 * identity that any other class also has is not an identity. So the harvest is
 * gated on the class, not on a tag and not on a kill: the scythe is the tool
 * that takes matter with it, and choosing to carry one is choosing to be the
 * character who can craft.
 *
 * ## What it takes
 *
 * Exactly the cells the swing was already going to cut free. The gate is
 * `TerrainInteraction.destroy` — same radius, same `maxHardness`, same
 * `M.indestructible` refusal that `CellWorld.damageCell` applies — so a scythe
 * harvests precisely the matter a scythe could already destroy, and this changes
 * nothing about how much terrain a swing removes. Corpses are the bulk of it in
 * practice because corpses are what a scythe swings at: `entity/ai/enemyAI.ts`
 * writes a body back into the world as a blob of its own composition, right
 * where the killing blow landed.
 *
 * Two consequences worth naming, both intended:
 *
 *   - **Iron and brass bodies need a harder edge.** `reaper_scythe` cuts to
 *     hardness 3; IRON is 6 and BRASS is 5, so a construct's corpse is not
 *     harvestable with the bare weapon. Kinetic and corrosive modules raise
 *     `maxHardness` to 5-6 (`resolveWeapon` takes the max across modules), so
 *     the metals are a build decision rather than a wall. That is a progression,
 *     not a gap.
 *   - **The pouch refuses before the world does.** A full pouch takes nothing
 *     and leaves the cell standing, so `applyTerrain` runs afterwards exactly as
 *     it did before this file existed.
 *
 * ## What this route owns that the field does not (#131)
 *
 * `sim/attract.ts` refuses `Category.RIGID`, and a death writes its two blobs on
 * opposite sides of that refusal by construction. `corpseMaterial` picks the
 * body's *soft* part — 50 of the 54 enemies leave a corpse the field takes — and
 * `corpseDebris` filters `Category.RIGID` explicitly, so the hard fragments are
 * rigid for 39 of the 44 enemies that leave any. **This is the route to those.**
 * The hardness band above is therefore not an inconvenience at the edge of the
 * feature; it is the price of the half of a corpse the field cannot lift, and
 * CORPSE_STONE (hardness 5) sits in it beside IRON and BRASS.
 *
 * There is a third route and it is free: kill the enemy frozen and `killEnemy`
 * writes `shatteredInto` instead, which is grit the field takes — 42 of the 44.
 * `tests/corpseGather.test.ts` holds all three as two-way lists.
 *
 * Deterministic by construction: a fixed scan order and no `Rng` draw, because
 * consuming randomness here would shift every later roll in the frame and break
 * the seed-equality guarantee `docs/01` states.
 */

import { harvestMaterial } from '../meta/crafting';
import type { MaterialPouch } from '../meta/crafting';
import { M, VOID } from '../sim/materials';
import { MAT } from '../sim/materialData';
import type { CellWorld } from '../sim/world';
import { digRouteMul } from './resolve';
import type { TerrainInteraction } from './types';

export type { MaterialPouch };

/**
 * Cells one swing may pocket.
 *
 * Derived from the two numbers that already exist rather than picked: `POUCH_CAP`
 * is 240, so 12 fills an empty pouch in twenty clean swings — a couple of rooms
 * of deliberate harvesting, not one swing and not fifty. It is also well under
 * the 30-unit cost of the cheapest recipe, so no single swing ever buys a craft
 * outright, which is the whole reason `meta/crafting.ts` chose a carried pouch
 * over a seen-set.
 */
export const HARVEST_CELLS_PER_SWING = 12;

/** Lazily built id -> name map. `MAT` is only populated after registration. */
let byId: Map<number, string> | null = null;

/**
 * Cell id -> the pouch key that names it.
 *
 * Exported for `combat/gather.ts` (#122), which is the second verb that has to
 * cross the same gap: `run.inventory.materials` is keyed by name and the world
 * is keyed by id, and a second lazily-built map would be a second thing to get
 * wrong the day `MAT` is populated later than someone expected.
 */
export function materialName(id: number): string | undefined {
  if (byId === null || byId.size === 0) {
    byId = new Map<number, string>();
    for (const name of Object.keys(MAT)) byId.set(MAT[name as keyof typeof MAT], name);
  }
  return byId.get(id);
}

/**
 * Reap the swing. Returns how many cells went into the pouch, which is the
 * number a caller would show — a harvest that silently took nothing is how a
 * player concludes the verb is broken.
 */
export function harvestSwing(
  cells: CellWorld,
  pouch: MaterialPouch,
  terrain: TerrainInteraction,
  wx: number,
  wy: number,
): number {
  const power = terrain.destroy.maxHardness;
  const radius = terrain.destroy.radius;
  if (power <= 0 || radius <= 0) return 0;

  const cx = Math.floor(wx);
  const cy = Math.floor(wy);
  const r = Math.ceil(radius);
  const r2 = radius * radius;
  let taken = 0;

  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (taken >= HARVEST_CELLS_PER_SWING) return taken;
      if (dx * dx + dy * dy > r2) continue;
      const x = cx + dx;
      const y = cy + dy;
      const m = cells.peek(x, y);
      if (m === VOID) continue;
      // The same three refusals `CellWorld.damageCell` makes, in the same order:
      // a swing cannot harvest what it could not have destroyed. The edge is
      // the routed one (#132) for the same reason — a scythe that can cut a
      // frozen wall must be able to take it, or the sentence above stops being
      // true the moment a route opens.
      if (M.indestructible[m] === 1) continue;
      if (M.hardness[m] > power * digRouteMul(m, cells.getFlags(x, y), terrain.damageType)) continue;

      const name = materialName(m);
      if (name === undefined) continue;
      if (harvestMaterial(pouch, name, 1) === 0) continue; // pouch full or capped
      cells.set(x, y, VOID);
      taken++;
    }
  }
  return taken;
}
